import { useEffect, useState } from 'react'
import { readPhoto } from '@/app/lib/local/watcher'
import { cachedThumb, thumbForPhoto } from '@/app/lib/local/thumbs'
import type { ImageFile } from '@/core/types'

/**
 * A photo's thumbnail URL.
 *
 * The agent build could put `agent.thumbUrl(path)` straight into an `<img src>`
 * because the thumbnail was a URL the server would render on demand. There is no
 * server, so a thumbnail is now rendered and cached locally and the URL only
 * exists once that has happened — which makes this asynchronous, and makes the
 * empty first render real rather than theoretical. `PhotoTile` already fades in
 * on load, so that gap was designed for.
 *
 * A thumbnail this tab has already resolved comes back on the FIRST render,
 * with no I/O. A tile remounts whenever the operator returns from Settings, and
 * starting it blank meant a grid of empty squares and one file open per photo
 * before any of them could find the thumbnail it already had.
 */
export function useThumb(file: ImageFile, width = 400): string | undefined {
  const [url, setUrl] = useState<string | undefined>(() => cachedThumb(file.identifier, width))

  useEffect(() => {
    const cached = cachedThumb(file.identifier, width)
    if (cached) {
      setUrl(cached)
      return
    }

    let cancelled = false

    void (async () => {
      const source = await readPhoto(file)
      // The operator moved or deleted it mid-session. The tile stays blank
      // rather than the grid failing.
      if (!source || cancelled) return

      try {
        const resolved = await thumbForPhoto(file.identifier, source, width)
        if (!cancelled) setUrl(resolved)
      } catch {
        // A frame the browser cannot decode must not take the gallery down.
      }
    })()

    return () => {
      cancelled = true
    }
  }, [file, width])

  return url
}
